/**
 * PDV Page - Main Component
 * Refactored with Clean Architecture pattern
 * Enhanced keyboard shortcuts (F1-F12)
 * Offline support with auto-sync
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useInstance } from '../../contexts/InstanceContext';
import { useOffline } from '../../contexts/OfflineContext';
import api, {
  productsAPI,
  categoriesAPI,
  combosAPI,
  ordersAPI,
  salesAPI,
  cashiersAPI,
  tablesAPI,
  customersAPI,
  paymentMethodsAPI,
} from '../../lib/api';
import { cn, generateId } from '../../lib/utils';
import { toMinor } from '../../lib/currency';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { ConnectionIndicator } from '../../components/ui/ConnectionIndicator';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Users,
  Table2,
  ArrowLeft,
  Sun,
  Moon,
  X,
  Package,
  Utensils,
  Wine,
  IceCream,
  Sandwich,
  UtensilsCrossed,
  QrCode,
  Banknote,
  Wallet,
  Keyboard,
  ClipboardList,
} from 'lucide-react';

// Modals
import CalculatorModal from './CalculatorModal';
import PaymentModal from './PaymentModal';
import DiscountModal from './DiscountModal';
import TableSelectModal from './TableSelectModal';
import CustomerSelectModal from './CustomerSelectModal';
import SplitBillModal from './SplitBillModal';
import VariationsModal from './VariationsModal';
import ShortcutsHelpModal from './ShortcutsHelpModal';
import OpenOrdersModal from './OpenOrdersModal';

// Hooks
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';

import { getLogo } from '../../lib/logos';

import './styles.css';

const iconMap = {
  Package, Utensils, Wine, IceCream, Sandwich, UtensilsCrossed, QrCode, Banknote, CreditCard, Wallet,
};

export default function PDV() {
  const { t } = useTranslation();
  const { cashierId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isAdminMaster, currentInstance, formatCurrency } = useInstance();
  const { isOnline, saveSaleOffline, pendingCount, cacheForOffline, getFromCache } = useOffline();
  const searchRef = useRef(null);

  // Data states
  const [cashier, setCashier] = useState(null);
  const [cashiers, setCashiers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [tables, setTables] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // UI states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('value');
  const [currentOrder, setCurrentOrder] = useState(null);

  // Modal states
  const [showCashierSelect, setShowCashierSelect] = useState(!cashierId);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showTableSelect, setShowTableSelect] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showSplitBill, setShowSplitBill] = useState(false);
  const [showVariations, setShowVariations] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showOpenOrders, setShowOpenOrders] = useState(false);

  // Payment state
  const [payments, setPayments] = useState([]);
  const [splitCount, setSplitCount] = useState(2);
  const [openOrders, setOpenOrders] = useState([]);

  // Barcode scanner integration - adds product directly to cart
  const handleBarcodeScanned = useCallback((code) => {
    // Search for product by barcode
    const product = products.find(p => 
      p.barcode === code || 
      p.ean === code || 
      p.sku === code
    );
    
    if (product) {
      // Check if product has variations
      if (product.variations && product.variations.length > 0) {
        setShowVariations(product);
      } else {
        // Add directly to cart (inline implementation to avoid circular dependency)
        const itemId = generateId();
        const price = product.price;
        
        setCart(prev => [...prev, {
          id: itemId,
          product_id: product.id,
          combo_id: null,
          name: product.name,
          quantity: 1,
          unit_price: price,
          variation_id: null,
          variation_name: null,
          discount: 0,
        }]);
        toast.success(`${product.name} adicionado via código de barras`);
      }
    } else {
      toast.error(`Produto não encontrado: ${code}`);
    }
  }, [products]);

  const { isListening, lastScan } = useBarcodeScanner({
    onScan: handleBarcodeScanned,
    autoStart: true,
    minLength: 4,
  });

  // Fetch data with offline cache support
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try to fetch from API
        const [catRes, prodRes, comboRes, tableRes, custRes, pmRes, cashRes] = await Promise.all([
          categoriesAPI.getAll(),
          productsAPI.getAll(),
          combosAPI.getAll(),
          tablesAPI.getAll(),
          customersAPI.getAll(),
          paymentMethodsAPI.getAll(),
          cashiersAPI.getAll(),
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
        setCombos(comboRes.data);
        setTables(tableRes.data);
        setCustomers(custRes.data);
        setPaymentMethods(pmRes.data);
        setCashiers(cashRes.data);

        if (cashierId) {
          const cashierData = cashRes.data.find(c => c.id === cashierId);
          if (cashierData) {
            setCashier(cashierData);
            if (cashierData.status !== 'open') {
              toast.error('Este caixa não está aberto');
              navigate('/cashiers');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(t('errors.generic'));
      }
    };
    fetchData();
  }, [cashierId, navigate, t]);

  // Load open orders (partial payments)
  const loadOpenOrders = useCallback(async () => {
    try {
      const res = await ordersAPI.getAll({ status: 'partial' });
      setOpenOrders(res.data || []);
    } catch (err) {
      console.error('Error loading open orders:', err);
    }
  }, []);

  useEffect(() => {
    if (cashier) {
      loadOpenOrders();
    }
  }, [cashier, loadOpenOrders]);

  const handleSelectOpenOrder = (order) => {
    setCurrentOrder(order);
    setCart(order.items || []);
    setSelectedTable(tables.find(t => t.id === order.table_id) || null);
    setSelectedCustomer(customers.find(c => c.id === order.customer_id) || null);
    setOrderDiscount(order.discount || 0);
    setDiscountType(order.discount_type || 'value');
    setShowOpenOrders(false);
    toast.success(`Pedido #${order.id?.slice(-6)} carregado`);
  };

  const closeAllModals = useCallback(() => {
    setShowCalculator(false);
    setShowPayment(false);
    setShowDiscount(false);
    setShowTableSelect(false);
    setShowCustomerSelect(false);
    setShowSplitBill(false);
    setShowVariations(null);
    setShowShortcuts(false);
    setShowOpenOrders(false);
  }, []);

  // Enhanced Keyboard shortcuts (F1-F12)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input (except for function keys and Escape)
      const isFunctionKey = e.key.startsWith('F') && e.key.length <= 3;
      if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') {
        if (e.key === 'Escape') {
          e.target.blur();
          setSearchQuery('');
        }
        if (!isFunctionKey) return;
      }

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          setShowShortcuts(true); // Help
          break;
        case 'F2':
          e.preventDefault();
          searchRef.current?.focus(); // Search
          break;
        case 'F3':
          e.preventDefault();
          setShowCustomerSelect(true); // Customer
          break;
        case 'F4':
          e.preventDefault();
          setShowCalculator(true); // Calculator
          break;
        case 'F5':
          e.preventDefault();
          // Refresh data
          window.location.reload();
          break;
        case 'F6':
          e.preventDefault();
          setShowDiscount(true); // Discount
          break;
        case 'F7':
          e.preventDefault();
          // Cancel last item
          if (cart.length > 0) {
            const lastItem = cart[cart.length - 1];
            setCart(prev => prev.slice(0, -1));
            toast.info(`${lastItem.name} removido`);
          }
          break;
        case 'F8':
          e.preventDefault();
          if (cart.length > 0) setShowPayment(true); // Finalize
          break;
        case 'F9':
          e.preventDefault();
          setShowTableSelect(true); // Table
          break;
        case 'F10':
          e.preventDefault();
          if (cart.length > 0) setShowSplitBill(true); // Split bill
          break;
        case 'F11':
          e.preventDefault();
          // Print order
          window.print();
          break;
        case 'F12':
          e.preventDefault();
          toggleTheme(); // Theme toggle
          break;
        case 'Escape':
          e.preventDefault();
          closeAllModals();
          break;
        case 'Delete':
          e.preventDefault();
          if (e.ctrlKey && cart.length > 0) {
            setCart([]);
            toast.info('Carrinho limpo');
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart, toggleTheme, closeAllModals]);

  // Get subcategory IDs for a parent category
  const getSubcategoryIds = (parentId) => {
    return categories
      .filter(c => c.parent_id === parentId)
      .map(c => c.id);
  };

  // Filter products - include products from subcategories
  const filteredProducts = products.filter(p => {
    if (selectedCategory === 'all') {
      return p.is_active && (!searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    
    // Check if product is in selected category OR in any subcategory of selected category
    const subcategoryIds = getSubcategoryIds(selectedCategory);
    const matchesCategory = p.category_id === selectedCategory || subcategoryIds.includes(p.category_id);
    const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && p.is_active;
  });

  // Group products by subcategory for display
  const groupedProducts = () => {
    if (selectedCategory === 'all') {
      return [{ category: null, products: filteredProducts }];
    }
    
    const subcategoryIds = getSubcategoryIds(selectedCategory);
    if (subcategoryIds.length === 0) {
      return [{ category: null, products: filteredProducts }];
    }
    
    // Products directly in parent category
    const parentProducts = filteredProducts.filter(p => p.category_id === selectedCategory);
    
    // Products grouped by subcategory
    const subcategoryGroups = subcategoryIds.map(subId => {
      const subCategory = categories.find(c => c.id === subId);
      return {
        category: subCategory,
        products: filteredProducts.filter(p => p.category_id === subId)
      };
    }).filter(g => g.products.length > 0);
    
    const result = [];
    if (parentProducts.length > 0) {
      result.push({ category: null, products: parentProducts });
    }
    result.push(...subcategoryGroups);
    return result;
  };

  const filteredCombos = selectedCategory === 'all' || selectedCategory === 'combos'
    ? combos.filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  // Cart functions
  const addToCart = (product, variation = null) => {
    const itemId = generateId();
    const price = product.price + (variation?.price_modifier || 0);
    
    setCart(prev => [...prev, {
      id: itemId,
      product_id: product.id,
      combo_id: null,
      name: product.name + (variation ? ` (${variation.name})` : ''),
      quantity: 1,
      unit_price: price,
      variation_id: variation?.id,
      variation_name: variation?.name,
      discount: 0,
    }]);
    
    setShowVariations(null);
    toast.success(`${product.name} adicionado`);
  };

  const addComboToCart = (combo) => {
    const itemId = generateId();
    setCart(prev => [...prev, {
      id: itemId,
      product_id: null,
      combo_id: combo.id,
      name: combo.name,
      quantity: 1,
      unit_price: combo.price,
      discount: 0,
    }]);
    toast.success(`${combo.name} adicionado`);
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.quantity * item.unit_price - item.discount), 0);
  const discountAmount = discountType === 'percent' ? subtotal * (orderDiscount / 100) : orderDiscount;
  const total = Math.max(0, subtotal - discountAmount);
  const paidAmount = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const remaining = total - paidAmount;

  // Payment functions
  const addPayment = (methodId) => {
    const method = paymentMethods.find(m => m.id === methodId);
    if (!method) return;
    
    // Calculate remaining considering current payments
    const currentPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const currentRemaining = total - currentPaid;
    
    // Use remaining amount if positive, otherwise 0 (user can adjust)
    const amount = currentRemaining > 0 ? currentRemaining : 0;
    setPayments(prev => [...prev, {
      payment_method_id: method.id,
      payment_method_name: method.name,
      amount,
    }]);
  };

  const updatePaymentAmount = (index, amount) => {
    // Allow empty string while user is typing, only parse when not empty
    const parsedAmount = amount === '' ? '' : (parseFloat(amount) || 0);
    setPayments(prev => prev.map((p, i) => i === index ? { ...p, amount: parsedAmount } : p));
  };

  const removePayment = (index) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  const handleFinalizeSale = async () => {
    // Calculate total paid including previous partial payments
    const previousPartialPaid = currentOrder?.partial_payments?.reduce(
      (sum, pp) => sum + (pp.amount || 0), 0
    ) || 0;
    const totalPaidNow = paidAmount + previousPartialPaid;

    if (totalPaidNow < total) {
      toast.error(`Valor pago insuficiente. Total: ${formatCurrency(total)}, Pago: ${formatCurrency(totalPaidNow)}`);
      return;
    }

    const saleData = {
      table_id: selectedTable?.id || null,
      customer_id: selectedCustomer?.id || null,
      cashier_id: cashier.id,
      user_id: user.id,
      items: cart,
      discount: orderDiscount,
      discount_type: discountType,
      subtotal,
      total,
      status: 'completed',
      partial_payments: currentOrder?.partial_payments || [],
      payments: [
        // Previous partial payments converted to sale payments
        ...(currentOrder?.partial_payments || []).flatMap(pp => 
          pp.payments?.map(p => ({
            payment_method_id: p.payment_method_id,
            payment_method_name: p.payment_method_name,
            amount: p.amount,
            payer_name: pp.payer_name,
          })) || []
        ),
        // Current payments
        ...payments,
      ],
    };

    // If offline, save locally
    if (!isOnline) {
      try {
        const localId = await saveSaleOffline(saleData);
        toast.success('Venda salva offline!', {
          description: 'Será sincronizada quando a conexão voltar.',
        });
        
        // Reset
        setCart([]);
        setPayments([]);
        setSelectedTable(null);
        setSelectedCustomer(null);
        setOrderDiscount(0);
        setCurrentOrder(null);
        setShowPayment(false);
        
        return;
      } catch (err) {
        console.error('Error saving sale offline:', err);
        toast.error('Erro ao salvar venda offline');
        return;
      }
    }

    // Online: proceed normally
    try {
      // Convert items to minor units
      const itemsWithMinor = cart.map(item => ({
        ...item,
        unit_price_minor: toMinor(item.unit_price),
        discount_minor: toMinor(item.discount || 0),
      }));
      
      const orderData = {
        table_id: selectedTable?.id || null,
        customer_id: selectedCustomer?.id || null,
        cashier_id: cashier.id,
        user_id: user.id,
        items: itemsWithMinor,
        discount_minor: toMinor(discountType === 'percent' ? discountAmount : orderDiscount),
        discount_type: discountType,
        subtotal_minor: toMinor(subtotal),
        total_minor: toMinor(total),
        status: 'open',
        partial_payments: currentOrder?.partial_payments || [],
      };

      let orderId;
      if (currentOrder) {
        await ordersAPI.update(currentOrder.id, orderData);
        orderId = currentOrder.id;
      } else {
        const orderRes = await ordersAPI.create(orderData);
        orderId = orderRes.data.id;
      }

      // Combine previous partial payments with current payments (convert to minor)
      const allPayments = [
        // Previous partial payments converted to sale payments
        ...(currentOrder?.partial_payments || []).flatMap(pp => 
          pp.payments?.map(p => ({
            payment_method_id: p.payment_method_id,
            payment_method_name: p.payment_method_name,
            amount: p.amount,
            payer_name: pp.payer_name,
          })) || []
        ),
        // Current payments
        ...payments,
      ];

      await salesAPI.create({
        order_id: orderId,
        payments: allPayments.length > 0 ? allPayments : payments,
      });

      toast.success('Venda finalizada com sucesso!');
      
      // Reset
      setCart([]);
      setPayments([]);
      setSelectedTable(null);
      setSelectedCustomer(null);
      setOrderDiscount(0);
      setCurrentOrder(null);
      setShowPayment(false);
      
      // Reload open orders
      loadOpenOrders();
    } catch (err) {
      console.error('Error finalizing sale:', err);
      
      // If network error, try to save offline
      if (!navigator.onLine || err.code === 'ERR_NETWORK') {
        try {
          await saveSaleOffline(saleData);
          toast.warning('Conexão perdida - Venda salva offline', {
            description: 'Será sincronizada automaticamente.',
          });
          
          // Reset
          setCart([]);
          setPayments([]);
          setSelectedTable(null);
          setSelectedCustomer(null);
          setOrderDiscount(0);
          setCurrentOrder(null);
          setShowPayment(false);
        } catch (offlineErr) {
          toast.error('Erro ao salvar venda');
        }
      } else {
        toast.error('Erro ao finalizar venda');
      }
    }
  };

  const handlePartialPayment = async (partialData) => {
    try {
      // Create or update order with partial payment info
      const orderData = {
        table_id: selectedTable?.id || null,
        customer_id: selectedCustomer?.id || null,
        cashier_id: cashier.id,
        user_id: user.id,
        items: cart,
        discount: orderDiscount,
        discount_type: discountType,
        subtotal,
        total,
        status: 'partial', // Keep order open
        partial_payments: [...(currentOrder?.partial_payments || []), {
          amount: partialData.amount,
          payer_name: partialData.payer_name,
          payments: partialData.payments,
          paid_at: new Date().toISOString(),
        }],
        notes: `Pagamento parcial de ${formatCurrency(partialData.amount)} por ${partialData.payer_name}`,
      };

      let orderId;
      if (currentOrder) {
        await ordersAPI.update(currentOrder.id, orderData);
        orderId = currentOrder.id;
      } else {
        const orderRes = await ordersAPI.create(orderData);
        orderId = orderRes.data.id;
        setCurrentOrder(orderRes.data);
      }

      toast.success(`Pagamento parcial de ${formatCurrency(partialData.amount)} registrado!`);
      
      // Reset payments for next partial
      setPayments([]);
      setShowPayment(false);
    } catch (err) {
      console.error('Error registering partial payment:', err);
      toast.error('Erro ao registrar pagamento parcial');
    }
  };

  const handleSplitBill = () => {
    const splitAmount = total / splitCount;
    const defaultMethod = paymentMethods[0];
    if (!defaultMethod) return;

    setPayments(Array(splitCount).fill(null).map(() => ({
      payment_method_id: defaultMethod.id,
      payment_method_name: defaultMethod.name,
      amount: parseFloat(splitAmount.toFixed(2)),
    })));
    setShowSplitBill(false);
    setShowPayment(true);
  };

  const handleSelectCashier = (selectedCashier) => {
    if (selectedCashier.status !== 'open') {
      toast.error('Este caixa não está aberto');
      return;
    }
    setCashier(selectedCashier);
    setShowCashierSelect(false);
    navigate(`/pdv/${selectedCashier.id}`);
  };

  // Cashier selection modal
  if (showCashierSelect || !cashier) {
    return (
      <div className="pdv-cashier-select" data-testid="pdv-cashier-select">
        <div className="pdv-cashier-select-content">
          <div className="pdv-cashier-select-header">
            <div className="pdv-cashier-select-logo">
              <img 
                src={getLogo(currentInstance, 'iconDark')} 
                alt="SisPDV" 
                className="h-10 w-10 object-contain"
                data-testid="pdv-logo"
              />
            </div>
            <h1>{t('pdv.selectCashier') || 'Selecione o Caixa'}</h1>
            <p className="text-muted-foreground">Escolha um caixa aberto para iniciar</p>
          </div>
          
          <div className="space-y-3">
            {cashiers.filter(c => c.status === 'open').map(c => (
              <Button
                key={c.id}
                variant="outline"
                className="w-full h-16 justify-start gap-4"
                onClick={() => handleSelectCashier(c)}
                data-testid={`select-cashier-${c.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">{c.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Saldo: {formatCurrency(c.current_amount)}
                  </p>
                </div>
              </Button>
            ))}
            
            {cashiers.filter(c => c.status === 'open').length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>Nenhum caixa aberto</p>
                <Button asChild className="mt-4">
                  <Link to="/cashiers">Gerenciar Caixas</Link>
                </Button>
              </div>
            )}
          </div>
          
          <Button variant="ghost" className="w-full mt-6" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar ao Dashboard
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdv-screen" data-testid="pdv-screen">
      {/* Header */}
      <header className="pdv-header no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/" data-testid="pdv-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="font-semibold">{cashier?.name || 'PDV'}</h1>
            <p className="text-xs text-muted-foreground">
              {user?.name} • {selectedTable ? `Mesa ${selectedTable.number}` : 'Balcão'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Barcode Scanner Status */}
          {isListening && (
            <Badge variant="outline" className="text-green-600 border-green-600 flex items-center gap-1">
              <QrCode className="h-3 w-3" />
              Scanner Ativo
            </Badge>
          )}
          
          {/* Connection Status */}
          <ConnectionIndicator variant="badge" showLabel />
          
          {/* Pending sync indicator */}
          {pendingCount > 0 && (
            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
              {pendingCount} pendente(s)
            </Badge>
          )}
          
          <Button variant="ghost" size="icon" onClick={() => setShowShortcuts(true)} data-testid="pdv-shortcuts-btn">
            <Keyboard className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="pdv-theme-toggle">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="pdv-main">
        {/* Categories Sidebar */}
        <aside className="pdv-categories no-print">
          <ScrollArea className="h-full">
            <button
              onClick={() => setSelectedCategory('all')}
              className={cn('pdv-category-btn', selectedCategory === 'all' && 'active')}
              data-testid="category-all"
            >
              <Package className="h-4 w-4" />
              <span>Todos</span>
            </button>
            {/* Show only parent categories (no parent_id) */}
            {categories.filter(c => c.is_active && !c.parent_id).map(cat => {
              const Icon = iconMap[cat.icon] || Package;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={cn('pdv-category-btn', selectedCategory === cat.id && 'active')}
                  data-testid={`category-${cat.id}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="truncate">{cat.name}</span>
                </button>
              );
            })}
            <button
              onClick={() => setSelectedCategory('combos')}
              className={cn('pdv-category-btn', selectedCategory === 'combos' && 'active')}
              data-testid="category-combos"
            >
              <Utensils className="h-4 w-4" />
              <span>Combos</span>
            </button>
          </ScrollArea>
        </aside>

        {/* Products Grid */}
        <main className="pdv-products">
          {/* Search */}
          <div className="pdv-search no-print">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchRef}
                placeholder={`${t('pdv.searchProduct')} (F2)`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="pdv-search"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {/* Mobile Categories */}
            <div className="pdv-categories-mobile lg:hidden">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
              >
                Todos
              </Button>
              {categories.filter(c => c.is_active).map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Products */}
          <ScrollArea className="flex-1 p-4">
            {groupedProducts().map((group, groupIdx) => (
              <div key={group.category?.id || 'main'} className="mb-6">
                {group.category && (
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <span className="text-sm font-semibold text-muted-foreground">
                      {group.category.name}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {group.products.length}
                    </Badge>
                  </div>
                )}
                <div className="pdv-product-grid">
                  {group.products.map(product => (
                    <button
                      key={product.id}
                      onClick={() => {
                        if (product.variations?.length > 0) {
                          setShowVariations(product);
                        } else {
                          addToCart(product);
                        }
                      }}
                      className="pdv-product-card"
                      data-testid={`product-${product.id}`}
                    >
                      {product.image_url && (
                        <div className="pdv-product-image">
                          <img src={product.image_url} alt={product.name} />
                        </div>
                      )}
                      <div className="p-3">
                        <h3 className="font-medium text-sm truncate">{product.name}</h3>
                        <p className="text-lg font-mono font-bold text-primary mt-1">
                          {formatCurrency(product.price)}
                        </p>
                        {product.stock <= product.min_stock && (
                          <Badge variant="destructive" className="text-xs mt-1">Estoque baixo</Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {/* Combos Section */}
            {filteredCombos.length > 0 && (selectedCategory === 'all' || selectedCategory === 'combos') && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                  <span className="text-sm font-semibold text-muted-foreground">
                    Combos
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {filteredCombos.length}
                  </Badge>
                </div>
                <div className="pdv-product-grid">
                  {filteredCombos.map(combo => (
                    <button
                      key={combo.id}
                      onClick={() => addComboToCart(combo)}
                      className="pdv-product-card"
                      data-testid={`combo-${combo.id}`}
                    >
                  <div className="pdv-combo-placeholder">
                    <Utensils className="h-12 w-12 text-primary/50" />
                  </div>
                  <div className="p-3">
                    <Badge className="mb-2">Combo</Badge>
                    <h3 className="font-medium text-sm truncate">{combo.name}</h3>
                    <p className="text-lg font-mono font-bold text-primary mt-1">
                      {formatCurrency(combo.price)}
                    </p>
                  </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Empty state */}
            {filteredProducts.length === 0 && filteredCombos.length === 0 && (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum produto encontrado</p>
                </div>
              </div>
            )}
          </ScrollArea>
        </main>

        {/* Cart Sidebar */}
        <aside className="pdv-cart no-print">
          {/* Cart Header */}
          <div className="pdv-cart-header">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">{t('pdv.cart')}</h2>
              {cart.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setCart([])}>
                  Limpar
                </Button>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              {selectedTable && (
                <Badge variant="secondary">
                  <Table2 className="h-3 w-3 mr-1" />
                  Mesa {selectedTable.number}
                </Badge>
              )}
              {selectedCustomer && (
                <Badge variant="secondary">
                  <Users className="h-3 w-3 mr-1" />
                  {selectedCustomer.name}
                </Badge>
              )}
            </div>
          </div>

          {/* Cart Items */}
          <ScrollArea className="flex-1">
            {cart.length === 0 ? (
              <div className="pdv-cart-empty">
                <Package className="h-12 w-12 mb-2 opacity-50" />
                <p>{t('pdv.emptyCart')}</p>
              </div>
            ) : (
              <div className="p-2">
                {cart.map(item => (
                  <div key={item.id} className="pdv-cart-item">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(item.unit_price)} un.
                        {item.discount > 0 && (
                          <span className="text-destructive ml-1">
                            (-{formatCurrency(item.discount)})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.id, -1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-6 text-center font-mono font-semibold">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.id, 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => removeFromCart(item.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Cart Footer */}
          <div className="pdv-cart-footer">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pdv.subtotal')}</span>
                <span className="font-mono">{formatCurrency(subtotal)}</span>
              </div>
              {orderDiscount > 0 && (
                <div className="flex justify-between text-destructive">
                  <span>{t('pdv.discount')}</span>
                  <span className="font-mono">-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span>{t('pdv.total')}</span>
                <span className="font-mono text-primary">{formatCurrency(total)}</span>
              </div>
            </div>
            <Button
              className="w-full h-12 text-lg"
              disabled={cart.length === 0}
              onClick={() => setShowPayment(true)}
              data-testid="pdv-checkout-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {t('pdv.payment')} <span className="kbd ml-2">F8</span>
            </Button>
          </div>
        </aside>
      </div>

      {/* Keyboard Shortcuts Bar */}
      <footer className="pdv-shortcuts-bar no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setShowShortcuts(true)}>
            <span className="kbd mr-2">F1</span> Ajuda
          </Button>
          <Button variant="ghost" size="sm" onClick={() => searchRef.current?.focus()}>
            <span className="kbd mr-2">F2</span> Buscar
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowCustomerSelect(true)}>
            <span className="kbd mr-2">F3</span> Cliente
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowCalculator(true)}>
            <span className="kbd mr-2">F4</span> Calc
          </Button>
          <Button 
            variant={openOrders.length > 0 ? 'secondary' : 'ghost'} 
            size="sm" 
            onClick={() => { loadOpenOrders(); setShowOpenOrders(true); }}
            className={openOrders.length > 0 ? 'relative' : ''}
            data-testid="open-orders-btn"
          >
            <ClipboardList className="h-4 w-4 mr-1" />
            Abertas
            {openOrders.length > 0 && (
              <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs">
                {openOrders.length}
              </Badge>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowDiscount(true)}>
            <span className="kbd mr-2">F6</span> Desc.
          </Button>
          <Button variant="ghost" size="sm" onClick={() => cart.length > 0 && setShowPayment(true)} disabled={cart.length === 0}>
            <span className="kbd mr-2">F8</span> Pagar
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowTableSelect(true)}>
            <span className="kbd mr-2">F9</span> Mesa
          </Button>
          <Button variant="ghost" size="sm" onClick={() => cart.length > 0 && setShowSplitBill(true)} disabled={cart.length === 0}>
            <span className="kbd mr-2">F10</span> Dividir
          </Button>
        </div>
        <div className="text-xs text-slate-400">
          <span className="kbd mr-1">ESC</span> Fechar • <span className="kbd mx-1">Ctrl+Del</span> Limpar
        </div>
      </footer>

      {/* Modals */}
      <ShortcutsHelpModal open={showShortcuts} onOpenChange={setShowShortcuts} />
      
      <CalculatorModal open={showCalculator} onOpenChange={setShowCalculator} />
      
      <PaymentModal
        open={showPayment}
        onOpenChange={setShowPayment}
        total={total}
        paidAmount={paidAmount}
        remaining={remaining}
        payments={payments}
        paymentMethods={paymentMethods}
        formatCurrency={formatCurrency}
        onAddPayment={addPayment}
        onUpdatePayment={updatePaymentAmount}
        onRemovePayment={removePayment}
        onFinalize={handleFinalizeSale}
        onPartialPayment={handlePartialPayment}
        onCloseAndContinue={() => setShowPayment(false)}
        previousPartialPaid={currentOrder?.partial_payments?.reduce((sum, pp) => sum + (pp.amount || 0), 0) || 0}
        partialPayments={currentOrder?.partial_payments || []}
      />
      
      <DiscountModal
        open={showDiscount}
        onOpenChange={setShowDiscount}
        discountType={discountType}
        setDiscountType={setDiscountType}
        orderDiscount={orderDiscount}
        setOrderDiscount={setOrderDiscount}
      />
      
      <TableSelectModal
        open={showTableSelect}
        onOpenChange={setShowTableSelect}
        tables={tables}
        selectedTable={selectedTable}
        onSelect={(table) => { setSelectedTable(table); setShowTableSelect(false); }}
      />
      
      <CustomerSelectModal
        open={showCustomerSelect}
        onOpenChange={setShowCustomerSelect}
        customers={customers}
        selectedCustomer={selectedCustomer}
        onSelect={(customer) => { setSelectedCustomer(customer); setShowCustomerSelect(false); }}
      />
      
      <SplitBillModal
        open={showSplitBill}
        onOpenChange={setShowSplitBill}
        total={total}
        splitCount={splitCount}
        setSplitCount={setSplitCount}
        formatCurrency={formatCurrency}
        onSplit={handleSplitBill}
      />
      
      <VariationsModal
        open={!!showVariations}
        onOpenChange={() => setShowVariations(null)}
        product={showVariations}
        formatCurrency={formatCurrency}
        onSelect={addToCart}
      />

      <OpenOrdersModal
        open={showOpenOrders}
        onOpenChange={setShowOpenOrders}
        orders={openOrders}
        formatCurrency={formatCurrency}
        onSelect={handleSelectOpenOrder}
        onRefresh={loadOpenOrders}
      />
    </div>
  );
}
